
import { defineComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import CommonDiagramCardNuxt from '~/components/common/CommonDiagramCardNuxt.vue'
import VueSlickCarousel from 'vue-slick-carousel'
import { UserType, Diagram } from '~/domain/interfaces'
import { getDiagramViewUrl, getProfileUrl } from '@helpers/CommunityHelper'
import { getUserJobAndLocation } from '@helpers/UserHelper'
import AppActions from '~/domain/constants/AppActions'
import { Template } from '~/domain/interfaces/TemplateTypes'
import { DIAGRAM_ACTIONS } from '~/domain/constants/DiagramConst'
import { instanceOfDiagram } from '~/domain/helpers'

import 'vue-slick-carousel/dist/vue-slick-carousel.css'
export default defineComponent({
  name: 'CommunityCardCarousel',
  components: { VueSlickCarousel, CommonDiagramCardNuxt },
  props: {
    title: {
      type: String,
      required: false,
    },
    subtitle: {
      type: String,
      required: false,
    },
    items: {
      type: Array,
      required: false,
      default: [] as (Diagram | UserType)[],
    },
    slideHeight: {
      type: String,
      required: false,
    },
    slideWidth: {
      type: String,
      required: false,
    },
    slideMargin: {
      type: String,
      required: false,
      default: '',
    },
    type: {
      type: String,
      required: false,
      default: '',
    },
    id: {
      type: String,
      default: 'diagram-card-carousel',
    },
  },
  data() {
    return {
      carouselSettingsControl: {
        arrows: false,
        dots: false,
        infinite: true,
        slidesToShow: 4,
        slidesToScroll: 1,
        pauseOnHover: true,
        draggable: false,
        cssEase: 'linear',
        variableWidth: true,
        touchMove: false,
      },
      pressedArrow: '',
      diagramActions: DIAGRAM_ACTIONS,
      menuVisible: false,
      menuPositionX: 0,
      menuPositionY: 0,
      menuDiagram: null as Diagram | null,
      menuItems: [] as any,
    }
  },
  computed: {
    ...mapGetters({
      userIsAuthenticated: 'userIsAuthenticated',
      authenticatedUser: 'authenticatedUser',
    }),
    ...mapState('contextMenu', {
      visibleMenu: 'visibleMenu',
    }),
    appUrl(): string {
      return this.$config.apiBaseURL || ''
    },
    cssVars() {
      return {
        '--slide-height': this.slideHeight,
        '--slide-width': this.slideWidth,
        '--slide-margin': parseInt(this.slideMargin.split('px')[0]) / 2 + 'px',
      }
    },
    highlightedItems() {
      return this.items?.map((item: any) => ({
        ...item,
        starred: true,
      }))
    },
  },
  methods: {
    showNext() {
      this.pressedArrow = 'next'
      ;(this.$refs.carousel as VueSlickCarousel).next()
      ;(this.$refs.carousel as VueSlickCarousel).pause()
    },

    showPrev() {
      this.pressedArrow = 'prev'
      ;(this.$refs.carousel as VueSlickCarousel).prev()
      ;(this.$refs.carousel as VueSlickCarousel).pause()
    },

    getItemUrl(item) {
      return this.type === 'diagram'
        ? getDiagramViewUrl(item)
        : getProfileUrl(item)
    },

    jobAndLocation(job: string, location?: string): string {
      return getUserJobAndLocation(job, location)
    },

    equalsAuthenticatedUser() {
      return this.$store.getters[`community/equalsAuthenticatedUser`]
    },

    async followUser(designer: UserType): Promise<void> {
      const isFollowed = !designer.isFollowed

      await this.$store.dispatch(AppActions.FOLLOW_DESIGNER, {
        designer,
        isFollowed,
        context: {
          ...this.$nuxt.context,
          $route: this.$route,
        },
      })
    },

    getMenuItems(diagram?: Diagram | Template) {
      const isLiked =
        this.userIsAuthenticated &&
        this.authenticatedUser &&
        diagram &&
        instanceOfDiagram(diagram)
          ? (diagram as Diagram)?.likedByAuthenticatedUser
          : false

      return [
        {
          text: this.$t('wordCapitalOpen'),
          action: DIAGRAM_ACTIONS.open,
          visible: true,
        },
        {
          text: this.$t('openInNewTab'),
          action: DIAGRAM_ACTIONS.openNewTab,
          visible: true,
        },
        { text: null, action: null, visible: true }, // separator
        {
          text: this.$t('copyLink'),
          action: DIAGRAM_ACTIONS.copyLink,
          visible: true,
        },
        {
          text: this.$t('makeACopy'),
          action: DIAGRAM_ACTIONS.makeACopy,
          visible: true,
        },
        {
          text: isLiked
            ? this.$t('wordCapitalUnlike')
            : this.$t('wordCapitalLike'),
          action: isLiked ? DIAGRAM_ACTIONS.unlike : DIAGRAM_ACTIONS.like,
          visible: true,
        },
      ]
    },

    async showMenu(evt: MouseEvent, diagram: Diagram) {
      if (
        this.visibleMenu[this.id] &&
        this.menuDiagram?.id &&
        diagram.id === this.menuDiagram.id
      ) {
        this.menuVisibleChanged(false)
      } else {
        this.menuDiagram = diagram
        this.menuPositionX = evt.clientX
        this.menuPositionY = evt.clientY + 10
        this.menuItems = this.getMenuItems(diagram)

        this.$store.commit('contextMenu/setIsUpdating', true)

        this.menuVisibleChanged(true)
      }
    },

    getDiagramViewUrl(diagram: Diagram | Template) {
      return getDiagramViewUrl(diagram)
    },

    menuVisibleChanged(visibility: boolean) {
      this.$store.commit('contextMenu/setIsVisible', {
        id: this.id,
        visible: visibility,
        target: this.menuDiagram,
      })
    },

    diagramAction(item: any): void {
      if (!item?.action || !item.diagram) return

      const diagramWebUrl = getDiagramViewUrl(this.menuDiagram as Diagram)

      switch (item.action) {
        case DIAGRAM_ACTIONS.like:
        case DIAGRAM_ACTIONS.unlike:
          this.likeDiagram(item)
          break
        case DIAGRAM_ACTIONS.open:
          window.open(diagramWebUrl, '_self')
          break
        case DIAGRAM_ACTIONS.openNewTab:
          window.open(diagramWebUrl, '_blank')
          break
        case DIAGRAM_ACTIONS.fork:
        case DIAGRAM_ACTIONS.makeACopy:
          window.open(item.diagram?.link, '_self')
          break
        case DIAGRAM_ACTIONS.copyLink:
          navigator.clipboard.writeText(
            (location && location.origin ? location.origin : '') + diagramWebUrl
          )
          break
        default:
          break
      }
    },

    async likeDiagram(item: any): Promise<void> {
      await this.$store.dispatch(AppActions.LIKE_DIAGRAM, {
        action: item.action,
        diagram: item.diagram,
        context: {
          ...this.$nuxt.context,
          $route: this.$route,
        },
      })
    },
  },
})
